import React, { useEffect, useState, useMemo, Fragment } from "react";
import { useDispatch } from "react-redux";
import { Row, Col, Card, Button, Table, Badge } from "react-bootstrap";
import { Fn_FillListData, Fn_GetReport } from "../../store/Functions";
import { API_WEB_URLS } from "../../constants/constAPI";
import PageTitle from "../layouts/PageTitle";
import jsPDF from "jspdf";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const currentYear = new Date().getFullYear();
const YEAR_LIST = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);

// Helper to format date into DD-MMM-YY (e.g., 01-Jan-05)
const formatDateDDMMMYY = (dateStr) => {
  if (!dateStr || dateStr.startsWith("1900-01-01")) return "N/A";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "N/A";
  const day = String(d.getDate()).padStart(2, "0");
  const monthNamesShort = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const month = monthNamesShort[d.getMonth()];
  const year = String(d.getFullYear()).slice(-2);
  return `${day}-${month}-${year}`;
};

// Helper to format date into DD/MM/YYYY
const formatDateDDMMYYYY = (dateStr) => {
  if (!dateStr || dateStr.startsWith("1900-01-01")) return "N/A";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "N/A";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear(); 
  return `${day}/${month}/${year}`;
};

const PfForm11NewJoining = () => {
  const dispatch = useDispatch();

  // Setup session state from localStorage
  const readSession = () => ({
    firm: (() => {
      try {
        return JSON.parse(localStorage.getItem("selectedFirm") || "null");
      } catch {
        return null;
      }
    })(),
    month: localStorage.getItem("selectedPayrollMonth") || "",
    year: parseInt(localStorage.getItem("selectedPayrollYear") || "0") || currentYear,
  });

  const [sessionFirm, setSessionFirm] = useState(() => readSession().firm);
  const [selectedMonth, setSelectedMonth] = useState(() => readSession().month);
  const [selectedYear, setSelectedYear] = useState(() => readSession().year);

  // Data lists & filters
  const [, setFirmList] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  
  // View Switcher: "register" (Tabular Master View) vs "document" (Statutory Form 11 Document)
  const [activeView, setActiveView] = useState("register");
  const [currentPageIndex, setCurrentPageIndex] = useState(0);

  // Table pagination
  const [tablePage, setTablePage] = useState(1);
  const [tablePageSize, setTablePageSize] = useState(15);

  // Sync session changes from storage and sessionChange events
  useEffect(() => {
    const onStorage = () => {
      const s = readSession();
      setSessionFirm(s.firm);
      setSelectedMonth(s.month);
      setSelectedYear(s.year);
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("sessionChange", onStorage);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("sessionChange", onStorage);
    };
  }, []);

  // Fetch initial base listings
  useEffect(() => {
    const firmApiUrl = `${API_WEB_URLS.MASTER}/0/token/${API_WEB_URLS.FirmMaster}/Id/0`;
    Fn_FillListData(dispatch, setFirmList, "gridData", firmApiUrl);
  }, [dispatch]);

  // Load report data when inputs change
  useEffect(() => {
    const firmId = sessionFirm?.Id || 0;
    if (firmId && selectedMonth && selectedYear) {
      loadReportData(firmId, selectedMonth, selectedYear);
    } else {
      setEmployees([]);
    }
  }, [sessionFirm, selectedMonth, selectedYear]);

  const loadReportData = async (firmId, monthName, year) => {
    setIsLoading(true);
    try {
      const monthNum = MONTH_NAMES.indexOf(monthName) + 1;
      const monthStr = String(monthNum);

      const fd = new FormData();
      fd.append("F_FirmMaster", firmId);
      fd.append("JoinMonth", monthStr);
      fd.append("JoinYear", year);

      const records = await Fn_GetReport(
        dispatch,
        () => {},
        "data",
        "GetPFForm11/0/token",
        { arguList: { id: 0, formData: fd } },
        true
      );

      let employeeData = [];
      if (records && records.length > 0) {
        if (records[0].Id !== undefined && records[0].Name !== undefined) {
          employeeData = records;
        } else {
          const firstKey = Object.keys(records[0])[0];
          if (firstKey !== undefined && firstKey !== null) {
            const rawVal = records[0][firstKey];
            if (typeof rawVal === "string") {
              const jsonString = records.map(row => row[firstKey] || "").join("");
              if (jsonString) {
                const rawObj = JSON.parse(jsonString);
                employeeData = rawObj.data || rawObj;
              }
            } else if (Array.isArray(rawVal)) {
              employeeData = rawVal;
            } else if (typeof rawVal === "object" && rawVal !== null) {
              employeeData = rawVal.data || rawVal;
            }
          }
        }
      }
      setEmployees(Array.isArray(employeeData) ? employeeData : (employeeData?.Records || employeeData?.Employees || []));
      setCurrentPageIndex(0);
      setTablePage(1);
    } catch (err) {
      console.error("Error loading employee data:", err);
      setEmployees([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Find Firm Code No. (which is PF No. on the branch) and Address
  const getFirmPfNo = () => {
    if (!sessionFirm || !sessionFirm.BranchData) return "N/A";
    try {
      const branches = JSON.parse(sessionFirm.BranchData);
      if (Array.isArray(branches) && branches.length > 0) {
        const mainBranch = branches.find(b => b.IsMainBranch === 1) || branches[0];
        return mainBranch.PFNo || "N/A";
      }
    } catch (e) {
      console.error("Error parsing branch data:", e);
    }
    return "N/A";
  };

  const getFirmAddress = () => {
    if (!sessionFirm || !sessionFirm.BranchData) return "N/A";
    try {
      const branches = JSON.parse(sessionFirm.BranchData);
      if (Array.isArray(branches) && branches.length > 0) {
        const mainBranch = branches.find(b => b.IsMainBranch === 1) || branches[0];
        return mainBranch.OfficeAddress || mainBranch.CoAddress || "N/A";
      }
    } catch (e) {
      console.error("Error parsing branch data:", e);
    }
    return "N/A";
  };

  // Filter employees whose name, father name, card no, or PF No matches search term
  const filteredNewJoiners = useMemo(() => {
    return employees.filter(emp => {
      const term = searchTerm.toLowerCase();
      return (
        (emp.Name && emp.Name.toLowerCase().includes(term)) ||
        (emp.FatherName && emp.FatherName.toLowerCase().includes(term)) ||
        (emp.CardNo && emp.CardNo.toLowerCase().includes(term)) ||
        (emp.PFNo && String(emp.PFNo).toLowerCase().includes(term)) ||
        (emp.UAN && String(emp.UAN).toLowerCase().includes(term))
      );
    });
  }, [employees, searchTerm]);

  // Executive KPI summary counts
  const kpiStats = useMemo(() => {
    const total = filteredNewJoiners.length;
    const pfCovered = filteredNewJoiners.filter(e => e.PFApplicable).length;
    const uanAllotted = filteredNewJoiners.filter(e => e.UAN && String(e.UAN).trim() !== "" && String(e.UAN) !== "0").length;
    const kycCompleted = filteredNewJoiners.filter(e => 
      (e.BankAccountNo && String(e.BankAccountNo).trim() !== "" && String(e.BankAccountNo) !== "0") ||
      (e.AadharNumber && String(e.AadharNumber).trim() !== "" && String(e.AadharNumber) !== "0") ||
      (e.PanCardNo && String(e.PanCardNo).trim() !== "" && String(e.PanCardNo) !== "0")
    ).length;
    return { total, pfCovered, uanAllotted, kycCompleted };
  }, [filteredNewJoiners]);

  // Table pagination slicing
  const paginatedEmployees = useMemo(() => {
    if (tablePageSize === "all") return filteredNewJoiners;
    const size = parseInt(tablePageSize, 10);
    const start = (tablePage - 1) * size;
    return filteredNewJoiners.slice(start, start + size);
  }, [filteredNewJoiners, tablePage, tablePageSize]);

  const totalPages = tablePageSize === "all" ? 1 : Math.ceil(filteredNewJoiners.length / parseInt(tablePageSize, 10)) || 1;

  // Reset pagination if filtered data changes
  useEffect(() => {
    setTablePage(1);
    if (currentPageIndex >= filteredNewJoiners.length && filteredNewJoiners.length > 0) {
      setCurrentPageIndex(0);
    }
  }, [filteredNewJoiners.length, currentPageIndex]);

  // Export to Excel / CSV
  const exportToCSV = () => {
    if (filteredNewJoiners.length === 0) {
      alert("No data available to export.");
      return;
    }

    const headers = [
      "S.No",
      "Card / Emp No",
      "Employee Name",
      "Father's / Husband's Name",
      "Gender",
      "Date of Birth",
      "PF Joining Date",
      "PF Account No",
      "UAN Number",
      "Bank Account No",
      "IFSC Code",
      "Aadhaar Number",
      "PAN Card No",
      "Mobile No",
      "Previous PF Member",
      "Status"
    ];

    const rows = filteredNewJoiners.map((emp, idx) => [
      idx + 1,
      `"${emp.CardNo || emp.EmployeeCode || ""}"`,
      `"${emp.Name || ""}"`,
      `"${emp.FatherName || ""}"`,
      emp.Sex || "",
      formatDateDDMMYYYY(emp.DateOfBirth),
      formatDateDDMMYYYY(emp.PFJoinDate || emp.JoinDate),
      `"${emp.PFNo || ""}"`,
      `"${emp.UAN || ""}"`,
      `"${emp.BankAccountNo || ""}"`,
      `"${emp.IFSCCode || ""}"`,
      `"${emp.AadharNumber || emp.AadharNo || ""}"`,
      `"${emp.PanCardNo || ""}"`,
      `"${emp.MobileNo || ""}"`,
      emp.OldMemberPF ? "Yes" : "No",
      emp.Status || "Working"
    ]);

    const csvContent = "\uFEFF" + [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `PF_Form11_New_Joiners_${selectedMonth}_${selectedYear}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Helper for rendering PDF of 1 or all employees
  const generatePDFDocument = (empList, fileName) => {
    const pdf = new jsPDF("p", "mm", "a4");

    empList.forEach((emp, index) => {
      if (index > 0) pdf.addPage();

      pdf.setFontSize(8);
      pdf.setFont("Helvetica", "normal");
      pdf.text("Employees' Provident Fund Scheme, 1952 (Paragraph 34 & 57) & Employees Pension Scheme, 1995 (Paragraph 24)", 14, 10);
      pdf.setFontSize(8.5);
      pdf.text("Declaration by a person taking up employment in any establishment on which EPF Scheme, 1952 and/or EPS, 1995 is applicable.", 14, 14);
      
      pdf.setFont("Helvetica", "bold");
      pdf.setFontSize(11);
      pdf.text("New Form No. 11 - Declaration Form", 105, 20, { align: "center" });

      pdf.setFontSize(9);
      pdf.rect(14, 24, 182, 230); // Main outline box

      // Grid dividers
      pdf.line(14, 32, 196, 32);
      pdf.line(14, 40, 196, 40);
      pdf.line(14, 48, 196, 48);
      pdf.line(14, 56, 196, 56);
      pdf.line(14, 64, 196, 64);
      pdf.line(14, 72, 196, 72);
      pdf.line(14, 82, 196, 82);
      pdf.line(14, 90, 196, 90);
      pdf.line(14, 126, 196, 126);
      pdf.line(14, 150, 196, 150);
      pdf.line(14, 178, 196, 178);
      pdf.line(14, 218, 196, 218);

      // Left Labels & Right values
      pdf.text("1. Name of the Member", 16, 29);
      pdf.text(emp.Name?.toUpperCase() || "", 95, 29);

      pdf.text("2. Father's / Husband's Name", 16, 37);
      pdf.text(emp.FatherName?.toUpperCase() || "", 95, 37);

      pdf.text("3. Date of Birth", 16, 45);
      pdf.text(formatDateDDMMMYY(emp.DateOfBirth) || "", 95, 45);

      pdf.text("4. Gender: (Male/Female/Transgender)", 16, 53);
      pdf.text(emp.Sex?.toUpperCase() === "MALE" || emp.Sex?.toUpperCase() === "M" ? "Male" : emp.Sex?.toUpperCase() === "FEMALE" || emp.Sex?.toUpperCase() === "F" ? "Female" : emp.Sex || "", 95, 53);

      pdf.text("5. Marital Status: (Married/UnMarried/Widow/Divorcee)", 16, 61);
      pdf.text(emp.MaritalStatus || "Un-Married", 95, 61);

      pdf.text("6. A. Email ID:", 16, 69);
      pdf.text(emp.Email || emp.EmailID || "N/A", 45, 69);
      pdf.text("B. Mobile No.:", 110, 69);
      pdf.text(emp.MobileNo || "N/A", 145, 69);

      pdf.text("7. Whether earlier a member of Employee's Provident Fund Scheme, 1952", 16, 77);
      pdf.text(emp.OldMemberPF ? "True" : "False", 160, 77);

      pdf.text("8. Whether earlier a member of Employee's Pension Scheme, 1995", 16, 86);
      pdf.text(emp.OldMemberPension ? "True" : "False", 160, 86);

      // Section 9
      pdf.text("9. Previous employment details : [if Yes to 7 AND/OR 8 Above ]", 16, 94);
      pdf.text("a) Universal Account Number (UAN) :", 20, 100);
      pdf.text(emp.OldUAN || "N/A", 95, 100);
      pdf.text("b) Previous PF Account Number :", 20, 106);
      pdf.text(emp.PrevPFNo || "N/A", 95, 106);
      pdf.text("c) Date of exit from previous employment: (DD/MM/YYYY)", 20, 112);
      pdf.text(formatDateDDMMYYYY(emp.DateOfExitPr) || "N/A", 95, 112);
      pdf.text("d) Scheme Certificate No. (if issued):", 20, 118);
      pdf.text("N A", 95, 118);
      pdf.text("e) Pension Payment Order (PPO) No. (if issued):", 20, 124);
      pdf.text("N A", 95, 124);

      // Section 10
      pdf.text("10. a) International Worker :", 16, 131);
      pdf.text(emp.IsInternationalEmployee ? "True" : "False", 95, 131);
      pdf.text("b) If Yes, State Country of Origin (India/Name of Country) :", 20, 137);
      pdf.text(emp.CountryOrigin || "N/A", 115, 137);
      pdf.text("c) Passport No. :", 20, 143);
      pdf.text(emp.PassportNo || "N/A", 95, 143);
      pdf.text("d) Validity of Passport :", 20, 149);
      pdf.text(formatDateDDMMYYYY(emp.PassportTill) || "N/A", 95, 149);

      // Section 11
      pdf.text("11. KYC Details: (attach self attested copies of following KYCs)", 16, 155);
      pdf.text("a) Bank Account No. & IFSC Code :", 20, 161);
      pdf.text(`${emp.BankAccountNo || "N/A"} & ${emp.IFSCCode || "N/A"}`, 95, 161);
      pdf.text("b) AADHAR Number :", 20, 167);
      pdf.text(emp.AadharNumber || emp.AadharNo || "0", 95, 167);
      pdf.text("c) Permanent Account Number (PAN), if available :", 20, 173);
      pdf.text(emp.PanCardNo || "0", 95, 173);

      // Section 12 Undertaking
      pdf.text("UNDERTAKING", 16, 183);
      pdf.setFontSize(7.5);
      pdf.text("1) Certified that the particulars are true to the best of my knowledge.", 18, 188);
      pdf.text("2) I authorize EPFO to use my Aadhar for verification/authentication/eKYC purpose for the service delivery.", 18, 193);
      pdf.text("3) Kindly transfer the funds and service details, if applicable, from the previous PF account as declared above to the present", 18, 198);
      pdf.text("P.F. Account. (The transfer would be possible only if the identified KYC detail approved by previous employer has been", 18, 202);
      pdf.text("verified by present employer using his Digital Signature Certificate.)", 18, 206);
      pdf.text("4) In case of changes in above details, the same will be intimated to employer at the earliest.", 18, 211);
      
      pdf.setFontSize(8.5);
      pdf.text("Date:", 18, 216);
      pdf.text("Place:", 80, 216);
      pdf.text("Signature of Member", 150, 216);

      // Section 13 Employer Declaration
      pdf.text("DECLARATION BY PRESENT EMPLOYER", 16, 224);
      pdf.setFontSize(8);
      pdf.text(`A) The member Mr./Ms./Mrs. ${emp.Name?.toUpperCase() || ""} has joined on ${formatDateDDMMMYY(emp.PFJoinDate || emp.JoinDate)} and has been allotted`, 18, 229);
      pdf.text(`PF NO. ${getFirmPfNo()}/${emp.SerialNo || ""}`, 18, 233);
      pdf.text("B) In case the person was earlier not a member of EPF Scheme, 1952 and EPS, 1995 :", 18, 238);
      pdf.text(`* (Post allotment of UAN) the UAN allotted for the member is: ${emp.UAN || "N/A"}`, 22, 242);
      pdf.text("* Please Tick the Appropriate Option : The KYC details of the above member in the UAN database:", 22, 246);
      pdf.text("[ ] Have not been uploaded.     [ ] Have been uploaded but not approved.     [ ] Have been uploaded and approved with DSC.", 25, 250);

      pdf.text("C) In case the person was earlier a member of EPF Scheme, 1952 and EPF, 1995 :", 18, 256);
      pdf.text("* the above PF Account Number/UAN of the member as mentioned in (A) above has been tagged with his/her UAN/", 22, 260);
      pdf.text("Previous Member ID as declared by member.", 22, 264);
      pdf.text("* Please Tick the Appropriate Option :", 22, 268);
      pdf.text("[ ] The KYC details of the above member in the UAN database have been approved with Digital Signature Certificate", 25, 272);
      pdf.text("and transfer request has been generated on portal.", 27, 276);
      pdf.text("[ ] As the DSC of establishment are not registered with EPFO, the member has been informed to file physical claim", 25, 280);
      pdf.text("(Form-13) for transfer of funds from his previous establishment.", 27, 284);

      pdf.text("Date:", 18, 290);
      pdf.text("Signature of Employer with Seal of Establishment", 125, 290);

      // Page footer
      pdf.setFontSize(8);
      pdf.text(`-- Page ${index + 1} of ${empList.length} --`, 105, 294, { align: "center" });
    });

    pdf.save(fileName);
  };

  const exportSinglePDF = () => {
    const emp = filteredNewJoiners[currentPageIndex];
    if (!emp) return;
    generatePDFDocument([emp], `PF_Form11_${(emp.Name || "Employee").replace(/\s+/g, "_")}_${selectedMonth}_${selectedYear}.pdf`);
  };

  const exportAllPDF = () => {
    if (filteredNewJoiners.length === 0) {
      alert("No data available to export.");
      return;
    }
    generatePDFDocument(filteredNewJoiners, `PF_Form11_All_Joiners_${selectedMonth}_${selectedYear}.pdf`);
  };

  const printStyles = `
    @media print {
      body * {
        visibility: hidden !important;
      }
      #print-form11-container, #print-form11-container * {
        visibility: visible !important;
      }
      #print-form11-container {
        display: block !important;
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        background: #fff !important;
        color: #000 !important;
      }
      .form11-page-break {
        page-break-after: always !important;
        break-after: page !important;
        padding: 20px !important;
      }
      .no-print {
        display: none !important;
      }
      .form11-print-table {
        border-collapse: collapse !important;
        width: 100% !important;
        margin-top: 10px !important;
      }
      .form11-print-table td {
        border: 1px solid #000 !important;
        padding: 5px 8px !important;
        font-size: 11px !important;
      }
    }
  `;

  const activeEmp = filteredNewJoiners[currentPageIndex];

  return (
    <Fragment>
      <style>{printStyles}</style>

      <div className="no-print">
        <PageTitle activeMenu="PF Form 11" motherMenu="Reports" pageContent="New Joining Statutory Declaration Suite" />
      </div>

      {/* Filter Card */}
      <Card className="no-print mb-4 shadow-sm border-0" style={{ borderRadius: "10px", background: "#ffffff" }}>
        <Card.Body className="p-3 p-md-4">
          <Row className="align-items-end g-3">
            {/* Firm Dropdown */}
            <Col lg={4} md={6}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>
                Firm <span className="badge bg-light text-success ms-1 border" style={{ fontSize: "11px" }}>🔒 Header Locked</span>
              </label>
              <input
                type="text"
                className="form-control"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px", backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
                value={sessionFirm?.Name || "Please select Firm from Header"}
                readOnly
                disabled
              />
            </Col>

            {/* Month Dropdown */}
            <Col lg={2} md={3}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>Month</label>
              <select
                className="form-select"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px" }}
                value={selectedMonth}
                onChange={(e) => {
                  setSelectedMonth(e.target.value);
                  if (e.target.value) localStorage.setItem("selectedPayrollMonth", e.target.value);
                  else localStorage.removeItem("selectedPayrollMonth");
                }}
              >
                <option value="">-- Month --</option>
                {MONTH_NAMES.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </Col>

            {/* Year Dropdown */}
            <Col lg={2} md={3}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>Year</label>
              <select
                className="form-select"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px" }}
                value={selectedYear}
                onChange={(e) => {
                  setSelectedYear(parseInt(e.target.value) || 0);
                  if (e.target.value) localStorage.setItem("selectedPayrollYear", e.target.value);
                  else localStorage.removeItem("selectedPayrollYear");
                }}
              >
                {YEAR_LIST.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </Col>

            {/* Live Search Input */}
            <Col lg={4} md={12}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>Quick Search</label>
              <input
                type="text"
                className="form-control"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px" }}
                placeholder="Search by name, father, card, or UAN..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {!sessionFirm?.Id ? (
        <Card className="shadow-sm border-0" style={{ borderRadius: "10px" }}>
          <Card.Body className="text-center p-5">
            <h5 className="text-warning mb-2 fw-semibold">⚠ Session Setup Required</h5>
            <p className="text-muted mb-0 fs-13">
              Please select a <strong>Firm</strong>, <strong>Month</strong>, and <strong>Year</strong> from the filters above or from the <strong>Dashboard Home</strong> to load the report.
            </p>
          </Card.Body>
        </Card>
      ) : isLoading ? (
        <Card className="shadow-sm border-0" style={{ borderRadius: "10px" }}>
          <Card.Body className="text-center p-5">
            <div className="spinner-border text-primary mb-3" role="status" />
            <h6 className="text-dark fw-bold">Loading New Joiners Statutory Data...</h6>
            <small className="text-muted">Fetching Form 11 declarations from EPF master database</small>
          </Card.Body>
        </Card>
      ) : filteredNewJoiners.length === 0 ? (
        <Card className="shadow-sm border-0" style={{ borderRadius: "10px" }}>
          <Card.Body className="text-center p-5">
            <div className="mb-3 text-muted" style={{ fontSize: "42px" }}>📂</div>
            <h5 className="text-dark mb-2 fw-semibold">No New Joiners Found</h5>
            <p className="text-muted mb-0 fs-13">
              There are no employees who joined during <strong>{(selectedMonth || "").toUpperCase()} {selectedYear}</strong> for this firm.
            </p>
          </Card.Body>
        </Card>
      ) : (
        <Fragment>
          {/* ─── DUAL VIEW SWITCHER TABS & ACTION BUTTONS ─── */}
          <div className="no-print d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
            {/* View Switcher Buttons */}
            <div className="d-flex gap-2 p-1 bg-white border rounded shadow-xs" role="group">
              <Button
                size="sm"
                className="px-3 fw-semibold fs-13"
                style={{
                  borderRadius: "6px",
                  backgroundColor: activeView === "register" ? "#1e3c72" : "#ffffff",
                  color: activeView === "register" ? "#ffffff" : "#334155",
                  border: activeView === "register" ? "1px solid #1e3c72" : "1px solid #cbd5e1",
                  boxShadow: activeView === "register" ? "0 2px 4px rgba(30,60,114,0.25)" : "none"
                }}
                onClick={() => setActiveView("register")}
              >
                📋 New Joiners Register (Table View)
                <span
                  className="ms-2 px-2 py-0 rounded-pill fw-bold"
                  style={{
                    fontSize: "11px",
                    backgroundColor: activeView === "register" ? "rgba(255,255,255,0.25)" : "#e2e8f0",
                    color: activeView === "register" ? "#ffffff" : "#1e293b"
                  }}
                >
                  {filteredNewJoiners.length}
                </span>
              </Button>
              <Button
                size="sm"
                className="px-3 fw-semibold fs-13"
                style={{
                  borderRadius: "6px",
                  backgroundColor: activeView === "document" ? "#1e3c72" : "#ffffff",
                  color: activeView === "document" ? "#ffffff" : "#334155",
                  border: activeView === "document" ? "1px solid #1e3c72" : "1px solid #cbd5e1",
                  boxShadow: activeView === "document" ? "0 2px 4px rgba(30,60,114,0.25)" : "none"
                }}
                onClick={() => setActiveView("document")}
              >
                📄 Statutory Form 11 (Document View)
              </Button>
            </div>

            {/* Global Actions */}
            <div className="d-flex gap-2">
              <Button
                size="sm"
                className="px-3 fw-semibold fs-12 shadow-xs"
                style={{ borderRadius: "6px", backgroundColor: "#15803d", color: "#ffffff", border: "none" }}
                onClick={exportToCSV}
              >
                <i className="fa fa-file-excel-o me-1 text-white" /> Export Excel / CSV
              </Button>
              <Button
                size="sm"
                className="px-3 fw-semibold fs-12 shadow-xs"
                style={{ borderRadius: "6px", backgroundColor: "#dc2626", color: "#ffffff", border: "none" }}
                onClick={exportAllPDF}
              >
                <i className="fa fa-file-pdf-o me-1 text-white" /> Download All PDFs
              </Button>
              <Button
                size="sm"
                className="px-3 fw-semibold fs-12 shadow-xs"
                style={{ borderRadius: "6px", backgroundColor: "#334155", color: "#ffffff", border: "none" }}
                onClick={() => window.print()}
              >
                <i className="fa fa-print me-1 text-white" /> Print
              </Button>
            </div>
          </div>

          {/* ═══════════════════════════════════════════════════════════ */}
          {/* TAB 1: 📋 NEW JOINERS REGISTER (DATA GRID VIEW)          */}
          {/* ═══════════════════════════════════════════════════════════ */}
          {activeView === "register" && (
            <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "10px", background: "#ffffff", overflow: "hidden" }}>
              <Card.Header className="bg-white border-bottom py-3 px-4 d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="mb-0 text-dark fw-bold fs-16">
                    New Joiners Register — {sessionFirm?.Name}
                  </h5>
                  <small className="text-muted fs-12">
                    Month: <strong>{selectedMonth} {selectedYear}</strong> &nbsp;|&nbsp; Showing <strong>{paginatedEmployees.length}</strong> of <strong>{filteredNewJoiners.length}</strong> Joiners
                  </small>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted fs-12">Show:</span>
                  <select
                    className="form-select form-select-sm"
                    style={{ width: "80px", borderRadius: "5px" }}
                    value={tablePageSize}
                    onChange={(e) => {
                      setTablePageSize(e.target.value);
                      setTablePage(1);
                    }}
                  >
                    <option value="10">10</option>
                    <option value="15">15</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="all">All</option>
                  </select>
                </div>
              </Card.Header>

              <Card.Body className="p-0">
                <div className="table-responsive">
                  <Table hover className="align-middle mb-0 text-nowrap" style={{ fontSize: "12px" }}>
                    <thead style={{ background: "#f8f9fa", borderBottom: "2px solid #dee2e6" }}>
                      <tr>
                        <th className="text-center py-2 px-3" style={{ width: "50px" }}>#</th>
                        <th className="py-2">Card No / Code</th>
                        <th className="py-2">Employee Name & Father</th>
                        <th className="py-2">DOB & Gender</th>
                        <th className="py-2">Joining Date</th>
                        <th className="py-2">PF A/C No</th>
                        <th className="py-2">UAN</th>
                        <th className="py-2">Bank & KYC Info</th>
                        <th className="text-center py-2">Prev PF</th>
                        <th className="text-center py-2" style={{ width: "120px" }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedEmployees.map((emp, index) => {
                        const globalIndex = tablePageSize === "all" 
                          ? index 
                          : (tablePage - 1) * parseInt(tablePageSize, 10) + index;
                        
                        const hasBank = Boolean(emp.BankAccountNo && String(emp.BankAccountNo).trim() !== "" && String(emp.BankAccountNo) !== "0");
                        const hasAadhar = Boolean(emp.AadharNumber && String(emp.AadharNumber).trim() !== "" && String(emp.AadharNumber) !== "0");
                        const hasPan = Boolean(emp.PanCardNo && String(emp.PanCardNo).trim() !== "" && String(emp.PanCardNo) !== "0");

                        return (
                          <tr key={emp.Id || index} style={{ borderBottom: "1px solid #f1f2f6" }}>
                            <td className="text-center fw-semibold text-muted">{globalIndex + 1}</td>
                            <td>
                              <span className="badge border fw-medium px-2 py-1" style={{ backgroundColor: "#f8f9fa", color: "#334155" }}>
                                {emp.CardNo || emp.EmployeeCode || `ID: ${emp.Id}`}
                              </span>
                            </td>
                            <td>
                              <div className="fw-bold text-dark">{emp.Name?.toUpperCase()}</div>
                              <small className="text-muted">S/o: {emp.FatherName?.toUpperCase() || "—"}</small>
                            </td>
                            <td>
                              <div>{formatDateDDMMMYY(emp.DateOfBirth)}</div>
                              <small className="text-muted">{emp.Sex || "—"}</small>
                            </td>
                            <td>
                              <span className="fw-semibold" style={{ color: "#0f172a" }}>
                                {formatDateDDMMMYY(emp.PFJoinDate || emp.JoinDate)}
                              </span>
                            </td>
                            <td>
                              <span className="badge border fw-semibold px-2 py-1" style={{ backgroundColor: "#eff6ff", color: "#1d4ed8", borderColor: "#bfdbfe" }}>
                                {emp.PFNo || "N/A"}
                              </span>
                            </td>
                            <td>
                              {emp.UAN && String(emp.UAN).trim() !== "" && String(emp.UAN) !== "0" ? (
                                <span className="badge fw-bold px-2 py-1" style={{ backgroundColor: "#dcfce7", color: "#15803d", border: "1px solid #86efac" }}>
                                  {emp.UAN}
                                </span>
                              ) : (
                                <span className="badge fw-normal px-2 py-1" style={{ backgroundColor: "#fef3c7", color: "#b45309", border: "1px solid #fde68a" }}>
                                  Pending
                                </span>
                              )}
                            </td>
                            <td>
                              <div className="d-flex flex-column gap-1">
                                <small className="text-muted">
                                  {hasBank ? `${emp.BankName || "Bank"}: ${emp.BankAccountNo}` : "No Bank Added"}
                                </small>
                                <div className="d-flex gap-1">
                                  <span style={{ backgroundColor: hasBank ? "#15803d" : "#94a3b8", color: "#ffffff", fontSize: "10px", fontWeight: "600", padding: "2px 6px", borderRadius: "3px" }}>Bank</span>
                                  <span style={{ backgroundColor: hasAadhar ? "#15803d" : "#94a3b8", color: "#ffffff", fontSize: "10px", fontWeight: "600", padding: "2px 6px", borderRadius: "3px" }}>Aadhaar</span>
                                  <span style={{ backgroundColor: hasPan ? "#15803d" : "#94a3b8", color: "#ffffff", fontSize: "10px", fontWeight: "600", padding: "2px 6px", borderRadius: "3px" }}>PAN</span>
                                </div>
                              </div>
                            </td>
                            <td className="text-center">
                              {emp.OldMemberPF ? (
                                <span style={{ backgroundColor: "#0284c7", color: "#ffffff", fontSize: "11px", fontWeight: "600", padding: "3px 8px", borderRadius: "4px", display: "inline-block" }}>
                                  Yes ({emp.PrevPFNo || "Member"})
                                </span>
                              ) : (
                                <span style={{ backgroundColor: "#f1f5f9", color: "#475569", border: "1px solid #cbd5e1", fontSize: "11px", fontWeight: "500", padding: "3px 8px", borderRadius: "4px", display: "inline-block" }}>
                                  Fresh
                                </span>
                              )}
                            </td>
                            <td className="text-center">
                              <Button
                                size="sm"
                                className="px-2 py-1 fs-11 fw-semibold shadow-xs"
                                style={{ backgroundColor: "#1e3c72", color: "#ffffff", border: "none", borderRadius: "4px" }}
                                onClick={() => {
                                  setCurrentPageIndex(globalIndex);
                                  setActiveView("document");
                                }}
                              >
                                📄 Form 11
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>

                {/* Table Footer Pagination */}
                {tablePageSize !== "all" && totalPages > 1 && (
                  <div className="d-flex justify-content-between align-items-center p-3 border-top bg-light">
                    <small className="text-muted">
                      Page <strong>{tablePage}</strong> of <strong>{totalPages}</strong> ({filteredNewJoiners.length} total joiners)
                    </small>
                    <div className="btn-group" role="group">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        disabled={tablePage === 1}
                        onClick={() => setTablePage(p => Math.max(1, p - 1))}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        disabled={tablePage === totalPages}
                        onClick={() => setTablePage(p => Math.min(totalPages, p + 1))}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          )}

          {/* ═══════════════════════════════════════════════════════════ */}
          {/* TAB 2: 📄 STATUTORY FORM 11 (OFFICIAL EPFO DECLARATION)    */}
          {/* ═══════════════════════════════════════════════════════════ */}
          {activeView === "document" && (
            <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "10px", background: "#ffffff", overflow: "hidden" }}>
              {/* Document Navigation Bar */}
              <Card.Header className="d-flex flex-wrap justify-content-between align-items-center bg-white border-bottom py-3 px-4 gap-3">
                <div className="d-flex align-items-center gap-2">
                  <Button
                    size="sm"
                    className="px-2 py-1 fw-semibold fs-12"
                    style={{ backgroundColor: "#ffffff", color: "#1e293b", border: "1px solid #cbd5e1" }}
                    onClick={() => setActiveView("register")}
                  >
                    ← Back to Register
                  </Button>
                  <div>
                    <h6 className="mb-0 text-dark fw-bold fs-15">
                      Statutory Form 11 Viewer
                    </h6>
                    <small className="text-muted fs-11">
                      New Form No. 11 Declaration under EPF Scheme 1952 & EPS 1995
                    </small>
                  </div>
                </div>

                {/* Quick Employee Selector Dropdown & Slider Controls */}
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <span className="text-muted fs-12 fw-semibold">Select Employee:</span>
                  <select
                    className="form-select form-select-sm"
                    style={{ minWidth: "240px", maxWidth: "320px", height: "32px", fontSize: "12px", borderRadius: "5px" }}
                    value={currentPageIndex}
                    onChange={(e) => setCurrentPageIndex(parseInt(e.target.value, 10))}
                  >
                    {filteredNewJoiners.map((emp, idx) => (
                      <option key={emp.Id || idx} value={idx}>
                        {idx + 1}. {emp.Name?.toUpperCase()} ({emp.CardNo || emp.EmployeeCode || `PF: ${emp.PFNo || "N/A"}`})
                      </option>
                    ))}
                  </select>

                  <div className="btn-group">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      disabled={currentPageIndex === 0}
                      onClick={() => setCurrentPageIndex(prev => prev - 1)}
                    >
                      <i className="fa fa-chevron-left" />
                    </Button>
                    <span className="input-group-text bg-light text-dark fs-12 fw-semibold px-2">
                      {currentPageIndex + 1} / {filteredNewJoiners.length}
                    </span>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      disabled={currentPageIndex === filteredNewJoiners.length - 1}
                      onClick={() => setCurrentPageIndex(prev => prev + 1)}
                    >
                      <i className="fa fa-chevron-right" />
                    </Button>
                  </div>

                  <Button
                    size="sm"
                    className="px-3 fw-semibold fs-12 shadow-xs"
                    style={{ backgroundColor: "#dc2626", color: "#ffffff", border: "none" }}
                    onClick={exportSinglePDF}
                  >
                    <i className="fa fa-file-pdf-o me-1 text-white" /> Download PDF
                  </Button>
                  <Button
                    size="sm"
                    className="px-3 fw-semibold fs-12 shadow-xs"
                    style={{ backgroundColor: "#2563eb", color: "#ffffff", border: "none" }}
                    onClick={() => window.print()}
                  >
                    <i className="fa fa-print me-1 text-white" /> Print Form
                  </Button>
                </div>
              </Card.Header>

              {/* Document Sheet Display */}
              <Card.Body className="p-4" style={{ background: "#f4f6f9" }}>
                <div
                  className="bg-white p-4 p-md-5 mx-auto shadow-sm border"
                  style={{
                    maxWidth: "860px",
                    borderRadius: "4px",
                    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                    fontSize: "12px",
                    lineHeight: "1.6",
                    color: "#212529"
                  }}
                >
                  {/* Official Government / EPFO Header */}
                  <div className="text-center pb-3 border-bottom mb-4">
                    <div className="text-uppercase fw-bold text-muted mb-1" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>
                      Employees' Provident Fund Organisation (EPFO)
                    </div>
                    <div className="text-muted" style={{ fontSize: "11px" }}>
                      Employees' Provident Funds Scheme, 1952 (Paragraph 34 & 57) & Employees' Pension Scheme, 1995 (Paragraph 24)
                    </div>
                    <div className="text-muted" style={{ fontSize: "10.5px" }}>
                      Declaration by a person taking up employment in any establishment on which EPF Scheme, 1952 and/or EPS, 1995 is applicable
                    </div>
                    <h5 className="fw-bold mt-2 mb-1 text-dark" style={{ textDecoration: "underline" }}>
                      New Form No. 11 - Declaration Form
                    </h5>
                    <div className="text-muted fs-11 mt-1">
                      (To be retained by the Employer for future reference)
                    </div>
                  </div>

                  {/* Establishment Info Header Banner */}
                  <div className="p-3 mb-4 rounded border" style={{ background: "#f8f9fa", borderLeft: "4px solid #1e3c72" }}>
                    <Row className="g-2">
                      <Col sm={8}>
                        <div className="text-muted fs-11 text-uppercase fw-bold">Name of Establishment:</div>
                        <div className="fw-bold fs-13 text-dark">{sessionFirm?.Name || "ESTABLISHMENT NAME"}</div>
                        <div className="text-muted fs-11 mt-1">{getFirmAddress()}</div>
                      </Col>
                      <Col sm={4} className="text-sm-end">
                        <div className="text-muted fs-11 text-uppercase fw-bold">Establishment PF Code:</div>
                        <div className="fw-bold fs-13" style={{ color: "#1e3c72" }}>{getFirmPfNo()}</div>
                        <div className="text-muted fs-11 mt-1">
                          Member ID: <strong>{getFirmPfNo()}/{activeEmp?.SerialNo || activeEmp?.CardNo}</strong>
                        </div>
                      </Col>
                    </Row>
                  </div>

                  {/* Form 11 Core Statutory Table */}
                  <Table bordered size="sm" className="mb-4 align-middle" style={{ borderColor: "#ced4da" }}>
                    <tbody>
                      <tr>
                        <td className="fw-bold bg-light" style={{ width: "42%" }}>
                          1. Name of the Member (in Block Letters)
                        </td>
                        <td className="fw-bold text-dark fs-13">{activeEmp?.Name?.toUpperCase() || "—"}</td>
                      </tr>
                      <tr>
                        <td className="fw-bold bg-light">
                          2. Father's / Husband's Name
                        </td>
                        <td>{activeEmp?.FatherName?.toUpperCase() || "—"}</td>
                      </tr>
                      <tr>
                        <td className="fw-bold bg-light">
                          3. Date of Birth (DD-MMM-YYYY)
                        </td>
                        <td>{formatDateDDMMMYY(activeEmp?.DateOfBirth)}</td>
                      </tr>
                      <tr>
                        <td className="fw-bold bg-light">
                          4. Gender
                        </td>
                        <td>{activeEmp?.Sex?.toUpperCase() === "M" ? "Male" : activeEmp?.Sex?.toUpperCase() === "F" ? "Female" : activeEmp?.Sex || "Male"}</td>
                      </tr>
                      <tr>
                        <td className="fw-bold bg-light">
                          5. Marital Status
                        </td>
                        <td>{activeEmp?.MaritalStatus || "Un-Married"}</td>
                      </tr>
                      <tr>
                        <td className="fw-bold bg-light">
                          6. Contact Details
                        </td>
                        <td>
                          <div><strong>Email ID:</strong> {activeEmp?.Email || activeEmp?.EmailID || "N/A"}</div>
                          <div><strong>Mobile No:</strong> {activeEmp?.MobileNo || "N/A"}</div>
                        </td>
                      </tr>
                      <tr>
                        <td className="fw-bold bg-light">
                          7. Whether earlier a member of EPF Scheme, 1952?
                        </td>
                        <td>
                          <span style={{ backgroundColor: activeEmp?.OldMemberPF ? "#15803d" : "#64748b", color: "#ffffff", padding: "3px 10px", borderRadius: "4px", fontSize: "11px", fontWeight: "600", display: "inline-block" }}>
                            {activeEmp?.OldMemberPF ? "YES" : "NO"}
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <td className="fw-bold bg-light">
                          8. Whether earlier a member of EPS, 1995?
                        </td>
                        <td>
                          <span style={{ backgroundColor: activeEmp?.OldMemberPension ? "#15803d" : "#64748b", color: "#ffffff", padding: "3px 10px", borderRadius: "4px", fontSize: "11px", fontWeight: "600", display: "inline-block" }}>
                            {activeEmp?.OldMemberPension ? "YES" : "NO"}
                          </span>
                        </td>
                      </tr>

                      {/* Section 9 Header */}
                      <tr style={{ background: "#e9ecef" }}>
                        <td colSpan={2} className="fw-bold py-2 text-dark">
                          9. Previous Employment Details (if Yes to 7 and/or 8 above)
                        </td>
                      </tr>
                      <tr>
                        <td className="ps-4">a) Universal Account Number (UAN)</td>
                        <td>{activeEmp?.OldUAN || "N/A"}</td>
                      </tr>
                      <tr>
                        <td className="ps-4">b) Previous Member ID / PF Account No</td>
                        <td>{activeEmp?.PrevPFNo || "N/A"}</td>
                      </tr>
                      <tr>
                        <td className="ps-4">c) Date of Exit from Previous Employment</td>
                        <td>{formatDateDDMMYYYY(activeEmp?.DateOfExitPr) || "N/A"}</td>
                      </tr>
                      <tr>
                        <td className="ps-4">d) Scheme Certificate No / e) PPO No</td>
                        <td>N A</td>
                      </tr>

                      {/* Section 10 Header */}
                      <tr style={{ background: "#e9ecef" }}>
                        <td colSpan={2} className="fw-bold py-2 text-dark">
                          10. International Worker Declaration
                        </td>
                      </tr>
                      <tr>
                        <td className="ps-4">a) Whether an International Worker?</td>
                        <td>{activeEmp?.IsInternationalEmployee ? "YES" : "NO"}</td>
                      </tr>
                      <tr>
                        <td className="ps-4">b) Country of Origin & Passport Details</td>
                        <td>
                          Origin: {activeEmp?.CountryOrigin || "India"} &nbsp;|&nbsp; 
                          Passport: {activeEmp?.PassportNo || "N/A"} (Valid Till: {formatDateDDMMYYYY(activeEmp?.PassportTill)})
                        </td>
                      </tr>

                      {/* Section 11 Header */}
                      <tr style={{ background: "#e9ecef" }}>
                        <td colSpan={2} className="fw-bold py-2 text-dark">
                          11. KYC Details (self-attested copies attached)
                        </td>
                      </tr>
                      <tr>
                        <td className="ps-4">a) Bank Account No & IFSC Code</td>
                        <td className="fw-semibold">
                          {activeEmp?.BankAccountNo ? `${activeEmp?.BankAccountNo} (IFSC: ${activeEmp?.IFSCCode || "N/A"})` : "N/A"}
                        </td>
                      </tr>
                      <tr>
                        <td className="ps-4">b) Aadhaar Card Number</td>
                        <td>{activeEmp?.AadharNumber || activeEmp?.AadharNo || "N/A"}</td>
                      </tr>
                      <tr>
                        <td className="ps-4">c) Permanent Account Number (PAN)</td>
                        <td>{activeEmp?.PanCardNo || "N/A"}</td>
                      </tr>
                    </tbody>
                  </Table>

                  {/* Section 12: Statutory Undertaking */}
                  <div className="border p-3 rounded mb-4 bg-light">
                    <h6 className="fw-bold text-dark fs-12 mb-2">UNDERTAKING BY MEMBER</h6>
                    <ol className="ps-3 mb-3 text-muted" style={{ fontSize: "11px", lineHeight: "1.5" }}>
                      <li>Certified that the particulars are true to the best of my knowledge.</li>
                      <li>I authorize EPFO to use my Aadhaar details for verification/authentication/e-KYC purposes.</li>
                      <li>Kindly transfer the funds and service details from the previous PF account as declared above to the present PF Account.</li>
                      <li>In case of changes in the above details, the same will be intimated to the employer at the earliest.</li>
                    </ol>
                    <Row className="mt-4 pt-3 border-top text-dark" style={{ fontSize: "11px" }}>
                      <Col xs={4}>
                        <div><strong>Date:</strong> {formatDateDDMMYYYY(activeEmp?.PFJoinDate || activeEmp?.JoinDate)}</div>
                        <div className="mt-2"><strong>Place:</strong> Jodhpur</div>
                      </Col>
                      <Col xs={8} className="text-end">
                        <div style={{ height: "35px" }} />
                        <div className="fw-bold border-top d-inline-block pt-1 px-4">
                          Signature or Thumb Impression of Member
                        </div>
                      </Col>
                    </Row>
                  </div>

                  {/* Section 13: Present Employer Verification */}
                  <div className="border p-3 rounded bg-white" style={{ borderLeft: "4px solid #11998e" }}>
                    <h6 className="fw-bold text-dark fs-12 mb-2">DECLARATION BY PRESENT EMPLOYER</h6>
                    <p className="mb-2 text-dark" style={{ fontSize: "11.5px" }}>
                      A) The member <strong>{activeEmp?.Name?.toUpperCase()}</strong> has joined on <strong>{formatDateDDMMMYY(activeEmp?.PFJoinDate || activeEmp?.JoinDate)}</strong> and has been allotted PF No. <strong>{getFirmPfNo()}/{activeEmp?.SerialNo || activeEmp?.CardNo}</strong>.
                    </p>
                    <p className="mb-2 text-dark" style={{ fontSize: "11px" }}>
                      B) In case the person was earlier not a member of EPF Scheme, 1952 and EPS, 1995:<br />
                      &bull; Post allotment of UAN, the UAN allotted for the member is: <strong>{activeEmp?.UAN || "Under Processing"}</strong>.<br />
                      &bull; The KYC details of the member in the UAN database: <strong>Uploaded & Approved with DSC</strong>.
                    </p>
                    <Row className="mt-4 pt-3 border-top text-dark" style={{ fontSize: "11px" }}>
                      <Col xs={4}>
                        <div><strong>Date:</strong> {formatDateDDMMYYYY(activeEmp?.PFJoinDate || activeEmp?.JoinDate)}</div>
                      </Col>
                      <Col xs={8} className="text-end">
                        <div style={{ height: "35px" }} />
                        <div className="fw-bold border-top d-inline-block pt-1 px-4">
                          Signature of Employer with Seal of Establishment
                        </div>
                      </Col>
                    </Row>
                  </div>
                </div>
              </Card.Body>
            </Card>
          )}

          {/* ─── PHYSICAL PRINTING VIEW (Conjoined Individual Form 11 Sheets) ─── */}
          <div id="print-form11-container" className="d-none">
            {filteredNewJoiners.map((emp, index) => (
              <div key={emp.Id || index} className="form11-page-break">
                <div style={{ textAlign: "center", fontSize: "10px", lineHeight: "1.3" }}>
                  Employees' Provident Fund Scheme, 1952 (Paragraph 34 & 57) & Employees Pension Scheme, 1995 (Paragraph 24)
                  <br />
                  Declaration by a person taking up employment in any establishment on which EPF Scheme, 1952 and/or EPS, 1995 is applicable.
                  <br />
                  <strong style={{ fontSize: "13px", display: "block", margin: "6px 0" }}>New Form No. 11 - Declaration Form</strong>
                </div>

                <div style={{ border: "1px solid #000", padding: "6px 10px", margin: "8px 0", fontSize: "10px" }}>
                  <strong>Establishment:</strong> {sessionFirm?.Name?.toUpperCase()} &nbsp;|&nbsp; 
                  <strong>PF Code:</strong> {getFirmPfNo()} &nbsp;|&nbsp; 
                  <strong>Member PF No:</strong> {getFirmPfNo()}/{emp.SerialNo || emp.CardNo}
                </div>

                <table className="form11-print-table">
                  <tbody>
                    <tr>
                      <td style={{ width: "42%" }}><strong>1. Name of the Member</strong></td>
                      <td><strong>{emp.Name?.toUpperCase()}</strong></td>
                    </tr>
                    <tr>
                      <td><strong>2. Father's / Husband's Name</strong></td>
                      <td>{emp.FatherName?.toUpperCase() || "N/A"}</td>
                    </tr>
                    <tr>
                      <td><strong>3. Date of Birth</strong></td>
                      <td>{formatDateDDMMMYY(emp.DateOfBirth)}</td>
                    </tr>
                    <tr>
                      <td><strong>4. Gender</strong></td>
                      <td>{emp.Sex?.toUpperCase() === "M" ? "Male" : emp.Sex?.toUpperCase() === "F" ? "Female" : emp.Sex}</td>
                    </tr>
                    <tr>
                      <td><strong>5. Marital Status</strong></td>
                      <td>{emp.MaritalStatus || "Un-Married"}</td>
                    </tr>
                    <tr>
                      <td><strong>6. Contact Info</strong></td>
                      <td>
                        <strong>Email:</strong> {emp.Email || emp.EmailID || "N/A"} &nbsp;|&nbsp; <strong>Mobile:</strong> {emp.MobileNo || "N/A"}
                      </td>
                    </tr>
                    <tr>
                      <td><strong>7. PF Member Previously?</strong></td>
                      <td>{emp.OldMemberPF ? "YES" : "NO"}</td>
                    </tr>
                    <tr>
                      <td><strong>8. Pension Member Previously?</strong></td>
                      <td>{emp.OldMemberPension ? "YES" : "NO"}</td>
                    </tr>
                    <tr>
                      <td colSpan={2} style={{ background: "#eee", fontWeight: "bold" }}>9. Previous Employment Details</td>
                    </tr>
                    <tr>
                      <td style={{ paddingLeft: "20px" }}>a) Universal Account Number (UAN)</td>
                      <td>{emp.OldUAN || "N/A"}</td>
                    </tr>
                    <tr>
                      <td style={{ paddingLeft: "20px" }}>b) Previous PF Member ID</td>
                      <td>{emp.PrevPFNo || "N/A"}</td>
                    </tr>
                    <tr>
                      <td style={{ paddingLeft: "20px" }}>c) Date of Exit from Previous Establishment</td>
                      <td>{formatDateDDMMYYYY(emp.DateOfExitPr) || "N/A"}</td>
                    </tr>
                    <tr>
                      <td style={{ paddingLeft: "20px" }}>d) Scheme Certificate / e) PPO No.</td>
                      <td>N A</td>
                    </tr>
                    <tr>
                      <td colSpan={2} style={{ background: "#eee", fontWeight: "bold" }}>10. International Worker Details</td>
                    </tr>
                    <tr>
                      <td style={{ paddingLeft: "20px" }}>a) International Worker Status</td>
                      <td>{emp.IsInternationalEmployee ? "YES" : "NO"}</td>
                    </tr>
                    <tr>
                      <td style={{ paddingLeft: "20px" }}>b) Country of Origin / c) Passport No.</td>
                      <td>{emp.CountryOrigin || "India"} &nbsp;|&nbsp; {emp.PassportNo || "N/A"}</td>
                    </tr>
                    <tr>
                      <td style={{ paddingLeft: "20px" }}>d) Passport Expiry Date</td>
                      <td>{formatDateDDMMYYYY(emp.PassportTill) || "N/A"}</td>
                    </tr>
                    <tr>
                      <td colSpan={2} style={{ background: "#eee", fontWeight: "bold" }}>11. KYC Details</td>
                    </tr>
                    <tr>
                      <td style={{ paddingLeft: "20px" }}>a) Bank A/c No. & IFSC</td>
                      <td>{emp.BankAccountNo ? `${emp.BankAccountNo} (IFSC: ${emp.IFSCCode || "N/A"})` : "N/A"}</td>
                    </tr>
                    <tr>
                      <td style={{ paddingLeft: "20px" }}>b) Aadhaar Card Number</td>
                      <td>{emp.AadharNumber || emp.AadharNo || "N/A"}</td>
                    </tr>
                    <tr>
                      <td style={{ paddingLeft: "20px" }}>c) Permanent Account Number (PAN)</td>
                      <td>{emp.PanCardNo || "N/A"}</td>
                    </tr>
                  </tbody>
                </table>

                {/* Undertaking */}
                <div style={{ marginTop: "10px", border: "1px solid #000", padding: "6px 8px", fontSize: "10px" }}>
                  <strong>UNDERTAKING BY MEMBER</strong>
                  <div style={{ marginTop: "3px" }}>
                    1) Certified that the particulars are true to the best of my knowledge.<br />
                    2) I authorize EPFO to use my Aadhaar for eKYC purpose.<br />
                    3) Kindly transfer funds from previous PF account to present PF account.<br />
                    4) Changes in details will be immediately intimated.
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: "8px" }}>
                    <div>Date: {formatDateDDMMYYYY(emp.PFJoinDate || emp.JoinDate)}</div>
                    <div>Place: Jodhpur</div>
                    <div>Signature of Member: ________________</div>
                  </div>
                </div>

                {/* Present Employer Declaration */}
                <div style={{ marginTop: "10px", border: "1px solid #000", padding: "6px 8px", fontSize: "10px" }}>
                  <strong>DECLARATION BY PRESENT EMPLOYER</strong>
                  <div style={{ marginTop: "3px" }}>
                    A) Mr./Ms./Mrs. <strong>{emp.Name?.toUpperCase()}</strong> joined on <strong>{formatDateDDMMMYY(emp.PFJoinDate || emp.JoinDate)}</strong> and has been allotted PF No. <strong>{getFirmPfNo()}/{emp.SerialNo || emp.CardNo}</strong>.<br />
                    B) Post allotment of UAN, UAN is: <strong>{emp.UAN || "N/A"}</strong>. KYC details approved with DSC.
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: "8px" }}>
                    <div>Date: {formatDateDDMMYYYY(emp.PFJoinDate || emp.JoinDate)}</div>
                    <div>Signature of Employer with Seal: ________________</div>
                  </div>
                </div>

                <div style={{ textAlign: "center", fontSize: "10px", marginTop: "10px" }}>
                  -- Page {index + 1} of {filteredNewJoiners.length} --
                </div>
              </div>
            ))}
          </div>
        </Fragment>
      )}
    </Fragment>
  );
};

export default PfForm11NewJoining;
